import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import api from '@/lib/api';
import { EmptyState, LoadingShell } from '@/components/ui-bits';
import {
  MessageSquare, Send, ExternalLink, Phone, ImagePlus, X, UserPlus, Search, Mail, Zap,
  StickyNote, Star, Shuffle, CheckCircle2, LayoutList, Columns3, Reply, Users, CornerUpLeft,
} from 'lucide-react';
import { fmtRelative } from '@/lib/format';
import { toast } from 'sonner';
import ChatMedia, { MediaTypeIcon } from '@/components/ChatMedia';
import WaAvatar from '@/components/WaAvatar';
import { useRealtime } from '@/hooks/useRealtime';
import { useAuth } from '@/lib/auth-context';

/** Format time HH:mm for chat bubbles (WhatsApp-like). */
function fmtChatTime(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' });
  } catch {
    return '';
  }
}

/** Day separator label. */
function fmtDayLabel(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    const today = new Date();
    const yday = new Date();
    yday.setDate(today.getDate() - 1);
    if (d.toDateString() === today.toDateString()) return 'Hoy';
    if (d.toDateString() === yday.toDateString()) return 'Ayer';
    return d.toLocaleDateString('es-MX', { weekday: 'short', day: 'numeric', month: 'short' });
  } catch {
    return '';
  }
}

function dayKey(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toDateString();
  } catch {
    return '';
  }
}

function leadDisplayName(lead) {
  if (!lead) return '';
  return `${lead.first_name || ''} ${lead.last_name || ''}`.trim() || lead.phone_e164 || 'Contacto';
}

function isGroupLead(lead) {
  return !!(lead?.is_whatsapp_group || lead?.wa_group_jid);
}

function quotePreview(m) {
  if (!m) return '';
  if (m.quoted_body) return m.quoted_body;
  if (m.media_type === 'image') return '📷 Imagen';
  if (m.media_type === 'video') return '🎬 Video';
  if (m.media_type === 'audio') return '🎤 Audio';
  if (m.media_type === 'document') return '📄 Documento';
  return m.body || 'Mensaje';
}

export default function WhatsAppInboxPage() {
  const { user: me } = useAuth();
  const [searchParams] = useSearchParams();
  const [conversations, setConversations] = useState([]);
  const [active, setActive] = useState(null);
  const [messages, setMessages] = useState([]);
  const [body, setBody] = useState('');
  const [emailSubject, setEmailSubject] = useState('');
  const [loading, setLoading] = useState(true);
  const [templates, setTemplates] = useState([]);
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [users, setUsers] = useState([]);
  const [filter, setFilter] = useState('');
  const [attach, setAttach] = useState(null); // { file, preview, meta after upload }
  const [sending, setSending] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [waStatus, setWaStatus] = useState(null);
  const [emailStatus, setEmailStatus] = useState(null);
  const [sendChannel, setSendChannel] = useState('whatsapp'); // whatsapp | email | webchat
  const [visitorTyping, setVisitorTyping] = useState(false);
  const fileRef = useRef(null);
  const bottomRef = useRef(null);
  const activeIdRef = useRef(null);

  const [listFilter, setListFilter] = useState(() => searchParams.get('filter') || 'all'); // all | mine | unassigned | unread | waiting | team | resolved
  const [channelFilter, setChannelFilter] = useState('all'); // all | whatsapp | email | webchat
  const [queue, setQueue] = useState(null);
  const [chatSla, setChatSla] = useState({ warn_minutes: 5, critical_minutes: 15 });
  const [quickReplies, setQuickReplies] = useState([]);
  const [showQuick, setShowQuick] = useState(false);
  const [internalMode, setInternalMode] = useState(false);
  const [teams, setTeams] = useState([]);
  const [teamFilter, setTeamFilter] = useState('');
  const [skillFilter, setSkillFilter] = useState('');
  const [leadSkills, setLeadSkills] = useState('');
  const [csat, setCsat] = useState(null);
  const [closeMacros, setCloseMacros] = useState([]);
  const [closing, setClosing] = useState(false);
  const [includeResolved, setIncludeResolved] = useState(false);
  const [viewMode, setViewMode] = useState('list'); // list | board
  const [boardConvs, setBoardConvs] = useState([]); // full set for kanban
  const [chatType, setChatType] = useState('all'); // all | dm | group
  const [replyTo, setReplyTo] = useState(null); // message being quoted
  const msgRefs = useRef({});

  const loadInbox = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const isBoard = viewMode === 'board';
      const params = new URLSearchParams({
        limit: isBoard ? '150' : '100',
        filter: isBoard ? 'all' : listFilter,
        channel: channelFilter,
        sort: 'unread_first',
      });
      if (filter.trim()) params.set('q', filter.trim());
      if (teamFilter) params.set('team_id', teamFilter);
      if (skillFilter.trim()) params.set('skill', skillFilter.trim());
      if (chatType && chatType !== 'all') params.set('chat_type', chatType);
      if (isBoard || includeResolved || listFilter === 'resolved') params.set('include_resolved', 'true');
      const { data } = await api.get(`/whatsapp/inbox?${params}`);
      const convs = data.conversations || [];
      if (isBoard) {
        setBoardConvs(convs);
        // Keep list filter as visual columns; still show selected in detail from board
      } else {
        setConversations(convs);
      }
      setQueue(data.queue || null);
      if (data.chat_sla) {
        setChatSla({
          warn_minutes: data.chat_sla.warn_minutes ?? 5,
          critical_minutes: data.chat_sla.critical_minutes ?? 15,
        });
      } else if (data.queue?.warn_minutes) {
        setChatSla({
          warn_minutes: data.queue.warn_minutes,
          critical_minutes: data.queue.critical_minutes ?? 15,
        });
      }
      setWaStatus(data.wa_status || null);
      setEmailStatus(data.email_status || null);
      setActive((prev) => {
        if (prev) {
          // Keep selection; refresh lead fields (avatar, group flags) from list
          const fresh = convs.find((c) => c.lead?.id === prev.id);
          return fresh?.lead ? { ...prev, ...fresh.lead } : prev;
        }
        const want = searchParams.get('lead');
        if (want) {
          const found = convs.find((c) => c.lead?.id === want);
          if (found) return found.lead;
        }
        return convs[0]?.lead || null;
      });
    } finally {
      if (!silent) setLoading(false);
    }
  }, [searchParams, listFilter, filter, channelFilter, teamFilter, skillFilter, includeResolved, viewMode, chatType]);

  const loadMessages = async (leadId) => {
    const params = channelFilter !== 'all' ? `?channel=${channelFilter}` : '';
    const { data } = await api.get(`/whatsapp/messages/${leadId}${params}`);
    setMessages(data.messages);
    setCsat(data.csat || null);
    setLeadSkills((data.skills || []).join(', '));
    try {
      await api.post(`/whatsapp/messages/${leadId}/read`);
      setConversations((prev) =>
        prev.map((c) => (c.lead?.id === leadId ? { ...c, unread: 0, unread_whatsapp: 0, unread_email: 0 } : c)),
      );
    } catch {
      /* ignore */
    }
  };

  useEffect(() => {
    loadInbox();
    api.get('/whatsapp/templates').then((r) => setTemplates(r.data.templates)).catch(() => {});
    api.get('/email/templates').then((r) => setEmailTemplates(r.data.templates || [])).catch(() => {});
    api.get('/admin/users').then((r) => setUsers(r.data.users || [])).catch(() => {});
    api.get('/quick-replies').then((r) => setQuickReplies((r.data.items || []).filter((x) => x.enabled !== false))).catch(() => {});
    api.get('/teams?active_only=true').then((r) => setTeams(r.data.teams || [])).catch(() => {});
    api.get('/whatsapp/inbox/close-macros').then((r) => setCloseMacros(r.data.items || [])).catch(() => {});
  }, [loadInbox]);

  const applyQuickReply = async (item) => {
    if (!item) return;
    try {
      const params = active?.id ? `?lead_id=${active.id}` : '';
      const { data } = await api.get(`/quick-replies/${item.id}/preview${params}`);
      setBody(data.body || item.body || '');
      setShowQuick(false);
      toast.success(`Insertado: ${item.title}`);
    } catch {
      setBody(item.body || '');
      setShowQuick(false);
    }
  };

  // Expand /shortcut when typing
  useEffect(() => {
    if (!body.startsWith('/') || body.includes(' ') || !quickReplies.length) return undefined;
    const key = body.trim().toLowerCase();
    const match = quickReplies.find((q) => (q.shortcut || '').toLowerCase() === key);
    if (!match) return undefined;
    const t = setTimeout(() => applyQuickReply(match), 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [body, quickReplies, active?.id]);

  // Server-side filter via listFilter + q; keep client list as-is
  const filteredConversations = conversations;

  const assignLead = async (userId) => {
    if (!active?.id) return;
    setAssigning(true);
    try {
      const { data } = await api.patch(`/leads/${active.id}`, {
        assigned_to: userId || null,
      });
      const updated = data.lead || { ...active, assigned_to: userId || null };
      setActive((prev) => (prev ? { ...prev, assigned_to: updated.assigned_to ?? (userId || null) } : prev));
      setConversations((prev) =>
        prev.map((c) =>
          c.lead?.id === active.id
            ? { ...c, lead: { ...c.lead, assigned_to: updated.assigned_to ?? (userId || null) } }
            : c,
        ),
      );
      toast.success(userId ? 'Lead asignado' : 'Asignación quitada');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'No se pudo asignar');
    } finally {
      setAssigning(false);
    }
  };

  const claimLead = async (leadId) => {
    if (!leadId) return;
    setAssigning(true);
    try {
      await api.post(`/whatsapp/inbox/claim/${leadId}`);
      toast.success('Conversación tomada');
      setActive((prev) => (prev && prev.id === leadId ? { ...prev, assigned_to: me?.id } : prev));
      setConversations((prev) =>
        prev.map((c) =>
          c.lead?.id === leadId
            ? { ...c, lead: { ...c.lead, assigned_to: me?.id } }
            : c,
        ),
      );
      loadInbox(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'No se pudo tomar');
    } finally {
      setAssigning(false);
    }
  };

  const fmtWait = (sec) => {
    if (!sec || sec < 60) return `${sec || 0}s`;
    if (sec < 3600) return `${Math.floor(sec / 60)}m`;
    return `${Math.floor(sec / 3600)}h ${Math.floor((sec % 3600) / 60)}m`;
  };

  const warnSec = (chatSla.warn_minutes || 5) * 60;
  const critSec = (chatSla.critical_minutes || 15) * 60;
  const overWarn = queue?.waiting_over_warn ?? queue?.waiting_over_5m ?? 0;
  const overCrit = queue?.waiting_over_critical ?? queue?.waiting_over_15m ?? 0;

  useEffect(() => {
    activeIdRef.current = active?.id || null;
    setReplyTo(null);
    if (active) {
      loadMessages(active.id);
      // Refresh profile photo in background (1:1 or group)
      if (active.id && (active.phone_e164 || active.wa_group_jid)) {
        api.post(`/whatsapp/avatar/${active.id}`).then((r) => {
          const av = r.data?.wa_avatar;
          if (av && av !== active.wa_avatar) {
            setActive((prev) => (prev && prev.id === active.id ? { ...prev, wa_avatar: av } : prev));
            setConversations((prev) =>
              prev.map((c) =>
                c.lead?.id === active.id ? { ...c, lead: { ...c.lead, wa_avatar: av } } : c,
              ),
            );
          }
        }).catch(() => {});
      }
    }
  }, [active?.id]);

  // Backup poll (SSE is primary for new messages)
  useEffect(() => {
    const t = setInterval(() => {
      loadInbox(true);
      if (activeIdRef.current) loadMessages(activeIdRef.current);
    }, 20000);
    return () => clearInterval(t);
  }, [loadInbox]);

  useRealtime({
    message_in: (data) => {
      loadInbox(true);
      if (data?.lead_id && data.lead_id === activeIdRef.current) {
        loadMessages(data.lead_id);
      }
    },
  });

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const pickFile = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (file.size > 25 * 1024 * 1024) {
      toast.error('Archivo demasiado grande (máx 25 MB)');
      return;
    }
    const preview = file.type.startsWith('image/') ? URL.createObjectURL(file) : null;
    setAttach({ file, preview, meta: null });
  };

  const clearAttach = () => {
    if (attach?.preview) URL.revokeObjectURL(attach.preview);
    setAttach(null);
  };

  const uploadIfNeeded = async () => {
    if (!attach?.file) return null;
    if (attach.meta) return attach.meta;
    const fd = new FormData();
    fd.append('file', attach.file);
    fd.append('purpose', 'outbound');
    const { data } = await api.post('/whatsapp/media/upload', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    setAttach((a) => (a ? { ...a, meta: data } : a));
    return data;
  };

  // Visitor typing indicator (webchat)
  useEffect(() => {
    if (!active?.id || sendChannel !== 'webchat') {
      setVisitorTyping(false);
      return undefined;
    }
    let cancelled = false;
    const tick = async () => {
      try {
        const { data } = await api.get(`/webchat/typing/${active.id}`);
        if (!cancelled) setVisitorTyping(!!data.typing);
      } catch {
        if (!cancelled) setVisitorTyping(false);
      }
    };
    tick();
    const t = setInterval(tick, 2500);
    return () => { cancelled = true; clearInterval(t); };
  }, [active?.id, sendChannel]);

  // Agent typing ping while composing webchat
  useEffect(() => {
    if (!active?.id || sendChannel !== 'webchat' || !body.trim()) return undefined;
    const t = setTimeout(() => {
      api.post('/webchat/typing', { lead_id: active.id, typing: true }).catch(() => {});
    }, 300);
    const clear = setTimeout(() => {
      api.post('/webchat/typing', { lead_id: active.id, typing: false }).catch(() => {});
    }, 4000);
    return () => { clearTimeout(t); clearTimeout(clear); };
  }, [body, active?.id, sendChannel]);

  const send = async () => {
    if (!active) return;
    if (internalMode) {
      if (!body.trim()) {
        toast.error('Escribe la nota interna');
        return;
      }
      setSending(true);
      try {
        await api.post(`/whatsapp/inbox/internal-note/${active.id}`, { body: body.trim() });
        toast.success('Nota interna guardada');
        setBody('');
        setInternalMode(false);
        loadMessages(active.id);
      } catch (err) {
        toast.error(err?.response?.data?.detail || 'Error');
      } finally {
        setSending(false);
      }
      return;
    }
    if (sendChannel === 'webchat') {
      if (!body.trim() && !attach) {
        toast.error('Escribe un mensaje o adjunta imagen');
        return;
      }
      setSending(true);
      try {
        await api.post('/webchat/typing', { lead_id: active.id, typing: false }).catch(() => {});
        let media = null;
        if (attach) media = await uploadIfNeeded();
        const payload = { lead_id: active.id, body: body || '' };
        if (media) {
          payload.media_filename = media.media_filename;
          payload.media_type = media.media_type || 'image';
          payload.media_mimetype = media.media_mimetype;
        }
        await api.post('/webchat/send', payload);
        toast.success('Enviado por Webchat');
        setBody('');
        clearAttach();
        loadMessages(active.id);
        loadInbox(true);
      } catch (err) {
        toast.error(err?.response?.data?.detail || 'Error');
      } finally {
        setSending(false);
      }
      return;
    }
    if (sendChannel === 'email') {
      if (!body.trim() || !emailSubject.trim()) {
        toast.error('Asunto y cuerpo requeridos para email');
        return;
      }
      if (!active.email) {
        toast.error('Este lead no tiene email');
        return;
      }
      setSending(true);
      try {
        const { data } = await api.post('/email/send', {
          lead_id: active.id,
          to_email: active.email,
          subject: emailSubject,
          body_html: body.includes('<') ? body : body.replace(/\n/g, '<br/>'),
        });
        if (data.result?.sent) {
          toast.success('Email enviado');
          setBody('');
          setEmailSubject('');
        } else {
          toast.error(data.result?.reason || 'No se pudo enviar');
        }
        loadMessages(active.id);
        loadInbox(true);
      } catch (err) {
        toast.error(err?.response?.data?.detail || 'Error');
      } finally {
        setSending(false);
      }
      return;
    }
    if (!body.trim() && !attach) return;
    setSending(true);
    try {
      let media = null;
      if (attach) media = await uploadIfNeeded();
      const payload = { lead_id: active.id, body: body || '' };
      if (media) {
        payload.media_type = media.media_type;
        payload.media_filename = media.media_filename;
        payload.media_mimetype = media.media_mimetype;
      }
      if (replyTo?.id) {
        payload.reply_to_message_id = replyTo.id;
      }
      const { data } = await api.post('/whatsapp/send', payload);
      setBody('');
      clearAttach();
      setReplyTo(null);
      if (data.result.fallback && data.result.wa_link) {
        window.open(data.result.wa_link, '_blank');
        toast.warning('No se envió por la sesión QR', {
          description:
            data.result.error ||
            'WhatsApp no está conectado. Revisa Ajustes → WhatsApp o completa el envío en la pestaña que se abrió.',
        });
      } else if (data.result.fallback) {
        toast.warning(data.result.error || 'No se pudo enviar por WhatsApp');
      } else {
        toast.success(replyTo ? 'Respuesta enviada' : 'Enviado por WhatsApp');
      }
      loadMessages(active.id);
      loadInbox();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error');
    } finally {
      setSending(false);
    }
  };

  const startReply = (m) => {
    if (!m || m.channel === 'internal') return;
    setReplyTo(m);
    setSendChannel('whatsapp');
    setInternalMode(false);
  };

  const scrollToMessage = (id) => {
    if (!id) return;
    const el = msgRefs.current[id];
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      el.classList.add('ring-2', 'ring-primary/50');
      setTimeout(() => el.classList.remove('ring-2', 'ring-primary/50'), 1200);
    }
  };

  const sendTpl = async (tpl) => {
    if (!active) return;
    try {
      const { data } = await api.post('/whatsapp/send', {
        lead_id: active.id,
        template_id: tpl.id,
      });
      if (data.result.fallback && data.result.wa_link) window.open(data.result.wa_link, '_blank');
      toast.success('Plantilla enviada');
      loadMessages(active.id);
      loadInbox();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error');
    }
  };

  const previewBody = (m) => {
    if (m.media_type === 'image') return m.body && m.body !== '[imagen]' ? m.body : '📷 Imagen';
    if (m.media_type === 'video') return m.body && m.body !== '[video]' ? m.body : '🎬 Video';
    if (m.media_type === 'audio') return '🎤 Audio';
    if (m.media_type === 'document') return m.body || '📄 Documento';
    if (m.channel === 'email' && m.subject) return `✉ ${m.subject}`;
    return m.body || '—';
  };

  const channelBadge = (ch) => {
    if (ch === 'email') return <span className="text-[10px] px-1.5 py-0.5 rounded bg-sky-500/15 text-sky-600">Email</span>;
    if (ch === 'whatsapp') return <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-600">WA</span>;
    if (ch === 'webchat') return <span className="text-[10px] px-1.5 py-0.5 rounded bg-violet-500/15 text-violet-600">Chat</span>;
    if (ch === 'internal') return <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-800">Interna</span>;
    return null;
  };

  const saveLeadSkills = async () => {
    if (!active?.id) return;
    const skills = leadSkills.split(',').map((s) => s.trim()).filter(Boolean);
    try {
      const { data } = await api.patch(`/whatsapp/inbox/skills/${active.id}`, { skills });
      setLeadSkills((data.skills || []).join(', '));
      toast.success('Skills guardados');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error');
    }
  };

  const requestCsat = async () => {
    if (!active?.id) return;
    try {
      await api.post(`/whatsapp/inbox/request-csat/${active.id}`);
      toast.success('CSAT solicitado en webchat');
      loadMessages(active.id);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error');
    }
  };

  const assignBySkill = async () => {
    if (!active?.id) return;
    setAssigning(true);
    try {
      const firstSkill = leadSkills.split(',').map((s) => s.trim()).filter(Boolean)[0] || null;
      const { data } = await api.post(`/whatsapp/inbox/assign-skill/${active.id}`, {
        skill: firstSkill,
      });
      const to = data.assigned_to;
      toast.success(`Asignado por skill a ${data.agent?.name || data.agent?.email || 'agente'}`);
      setActive((prev) => (prev ? { ...prev, assigned_to: to } : prev));
      setConversations((prev) =>
        prev.map((c) =>
          c.lead?.id === active.id
            ? { ...c, lead: { ...c.lead, assigned_to: to } }
            : c,
        ),
      );
      loadInbox(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'No se pudo asignar por skill');
    } finally {
      setAssigning(false);
    }
  };

  const runCloseMacro = async (macroId) => {
    if (!active?.id) return;
    setClosing(true);
    try {
      const { data } = await api.post(`/whatsapp/inbox/close/${active.id}`, { macro_id: macroId });
      toast.success(`Cierre: ${(data.actions || []).join(', ') || 'ok'}`);
      if (data.lead) {
        setActive((prev) => (prev ? { ...prev, ...data.lead } : prev));
      }
      loadMessages(active.id);
      loadInbox(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error al cerrar');
    } finally {
      setClosing(false);
    }
  };

  const seedCloseMacros = async () => {
    try {
      const { data } = await api.post('/whatsapp/inbox/close-macros/seed');
      toast.success(data.created ? `${data.created} macros creadas` : 'Ya hay macros');
      const r = await api.get('/whatsapp/inbox/close-macros');
      setCloseMacros(r.data.items || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error');
    }
  };

  const reopenConversation = async () => {
    if (!active?.id) return;
    try {
      await api.post(`/whatsapp/inbox/reopen/${active.id}`);
      toast.success('Conversación reabierta en cola');
      setActive((prev) => {
        if (!prev) return prev;
        const next = { ...prev };
        delete next.inbox_resolved_at;
        delete next.inbox_resolved_by;
        return next;
      });
      loadInbox(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error');
    }
  };

  const boardColumns = (() => {
    const src = viewMode === 'board' ? boardConvs : conversations;
    const cols = {
      waiting: [],
      mine: [],
      unassigned: [],
      resolved: [],
    };
    src.forEach((c) => {
      const resolved = !!(c.resolved || c.lead?.inbox_resolved_at);
      if (resolved) {
        cols.resolved.push(c);
        return;
      }
      if (c.waiting) cols.waiting.push(c);
      if (c.lead?.assigned_to === me?.id) cols.mine.push(c);
      if (!c.lead?.assigned_to) cols.unassigned.push(c);
    });
    // waiting: longest first
    cols.waiting.sort((a, b) => (b.wait_seconds || 0) - (a.wait_seconds || 0));
    return cols;
  })();

  const [dragOverCol, setDragOverCol] = useState(null);
  const [draggingId, setDraggingId] = useState(null);

  const kanbanMove = async (leadId, column) => {
    if (!leadId || !column) return;
    try {
      const { data } = await api.post(`/whatsapp/inbox/kanban-move/${leadId}`, { column });
      toast.success(
        column === 'mine' ? 'Tomada'
          : column === 'unassigned' ? 'Liberada'
            : column === 'resolved' ? 'Resuelta'
              : 'Reabierta en cola',
      );
      if (data.lead && active?.id === leadId) {
        setActive((prev) => (prev ? { ...prev, ...data.lead } : prev));
      }
      loadInbox(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'No se pudo mover');
    }
  };

  const BoardCard = ({ c }) => {
    const group = isGroupLead(c.lead) || c.is_group;
    return (
    <div
      draggable
      onDragStart={(e) => {
        e.dataTransfer.setData('text/lead-id', c.lead.id);
        e.dataTransfer.effectAllowed = 'move';
        setDraggingId(c.lead.id);
      }}
      onDragEnd={() => { setDraggingId(null); setDragOverCol(null); }}
      onClick={() => setActive(c.lead)}
      className={`w-full text-left p-2.5 rounded-[10px] border mb-2 cursor-grab active:cursor-grabbing hover:bg-accent/60 ${
        active?.id === c.lead?.id ? 'border-primary bg-accent/40' : 'border-border'
      } ${draggingId === c.lead?.id ? 'opacity-50' : ''}`}
    >
      <div className="flex items-center gap-2">
        <WaAvatar path={c.lead.wa_avatar} name={leadDisplayName(c.lead)} isGroup={group} size={32} />
        <div className="text-sm font-medium truncate flex items-center gap-1 min-w-0 flex-1">
          {group && (
            <span className="text-[9px] px-1 rounded bg-emerald-500/15 text-emerald-700 shrink-0 inline-flex items-center gap-0.5">
              <Users className="h-2.5 w-2.5" /> Grupo
            </span>
          )}
          <span className="truncate">{leadDisplayName(c.lead)}</span>
        </div>
      </div>
      <div className="text-[11px] text-muted-foreground truncate flex items-center gap-1 mt-0.5">
        {(c.channels || []).includes('whatsapp') && <MessageSquare className="h-3 w-3 text-emerald-500" />}
        {(c.channels || []).includes('email') && <Mail className="h-3 w-3 text-sky-500" />}
        {(c.channels || []).includes('webchat') && <span className="text-[9px] px-1 rounded bg-violet-500/15 text-violet-600">Chat</span>}
        <span className="truncate">{previewBody(c.last_message || {})}</span>
      </div>
      <div className="flex items-center justify-between mt-1 text-[10px] text-muted-foreground">
        <span>{fmtRelative(c.last_message?.created_at)}</span>
        {c.waiting && c.wait_seconds > 0 && (
          <span className={c.wait_seconds >= critSec ? 'text-[hsl(var(--danger))] font-medium' : c.wait_seconds >= warnSec ? 'text-amber-600 font-medium' : ''}>
            ⏳ {fmtWait(c.wait_seconds)}
          </span>
        )}
        {c.unread > 0 && (
          <span className="min-w-[16px] h-4 px-1 rounded-full bg-primary text-primary-foreground text-[10px] font-bold grid place-items-center">
            {c.unread > 9 ? '9+' : c.unread}
          </span>
        )}
      </div>
    </div>
    );
  };

  return (
    <div className="flex flex-col flex-1 min-h-0 h-full">
      <div className="shrink-0 px-4 pt-3 pb-2 border-b border-border bg-card/40">
        <div className="flex items-center justify-between gap-3 mb-2">
          <h1 className="page-title">Inbox</h1>
          <div className="flex gap-1 p-0.5 rounded-xl bg-muted/80 border border-border">
            <button
              type="button"
              className={`h-8 w-8 grid place-items-center rounded-lg transition-colors ${viewMode === 'list' ? 'bg-card shadow-sm text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
              onClick={() => setViewMode('list')}
              title="Lista"
            >
              <LayoutList className="h-4 w-4" />
            </button>
            <button
              type="button"
              className={`h-8 w-8 grid place-items-center rounded-lg transition-colors ${viewMode === 'board' ? 'bg-card shadow-sm text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
              onClick={() => setViewMode('board')}
              title="Tablero"
            >
              <Columns3 className="h-4 w-4" />
            </button>
          </div>
        </div>
        {queue?.business_hours_enabled && queue?.within_business_hours === false && (
          <div className="mb-2 text-xs text-muted-foreground">Fuera de horario · SLA en pausa</div>
        )}
        {queue && (
          <div className="flex flex-wrap gap-1.5">
            {[
              { key: 'waiting', label: 'Espera', n: queue.waiting || 0 },
              { key: 'unassigned', label: 'Sin asignar', n: queue.unassigned || 0 },
              { key: 'unread', label: 'No leídos', n: queue.mine_unread || 0 },
              { key: 'all', label: 'Todos', n: queue.total || 0 },
            ].map((q) => (
              <button
                key={q.key}
                type="button"
                onClick={() => setListFilter(q.key)}
                className={`filter-pill ${listFilter === q.key ? 'filter-pill-active' : ''}`}
              >
                {q.label}
                <span className="tabular-nums opacity-70">{q.n}</span>
              </button>
            ))}
          </div>
        )}
        {waStatus?.mode === 'qr' && waStatus?.qr_status && waStatus.qr_status !== 'connected' && (
          <div className="mt-2 text-xs text-[hsl(var(--warning))]">
            WA: {waStatus.qr_status} · <Link to="/app/settings" className="underline">Ajustes</Link>
          </div>
        )}
      </div>
      {loading ? (
        <div className="flex-1 min-h-0"><LoadingShell /></div>
      ) : viewMode === 'board' ? (
        <div className="grid lg:grid-cols-[1fr_320px] gap-0 flex-1 min-h-0 border-t border-border">
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-2 min-h-0 overflow-hidden">
            {[
              { key: 'waiting', title: 'Espera', items: boardColumns.waiting, tone: 'border-t-amber-500' },
              { key: 'mine', title: 'Mías', items: boardColumns.mine, tone: 'border-t-primary' },
              { key: 'unassigned', title: 'Libres', items: boardColumns.unassigned, tone: 'border-t-sky-500' },
              { key: 'resolved', title: 'Hechas', items: boardColumns.resolved, tone: 'border-t-emerald-500' },
            ].map((col) => (
              <div
                key={col.key}
                className={`bg-card border border-border rounded-none flex flex-col min-h-0 border-t-2 ${col.tone} ${
                  dragOverCol === col.key ? 'ring-2 ring-primary/40 bg-accent/20' : ''
                }`}
                onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; setDragOverCol(col.key); }}
                onDragLeave={() => setDragOverCol((v) => (v === col.key ? null : v))}
                onDrop={(e) => {
                  e.preventDefault();
                  const leadId = e.dataTransfer.getData('text/lead-id');
                  setDragOverCol(null);
                  setDraggingId(null);
                  if (leadId) kanbanMove(leadId, col.key);
                }}
              >
                <div className="px-3 h-10 border-b border-border flex items-center justify-between shrink-0">
                  <span className="text-[13px] font-medium">{col.title}</span>
                  <span className="text-xs text-muted-foreground tabular-nums">{col.items.length}</span>
                </div>
                <div className="flex-1 overflow-y-auto p-2 min-h-[80px]">
                  {col.items.length === 0 && (
                    <div className="text-[11px] text-muted-foreground text-center py-6">
                      {dragOverCol === col.key ? 'Suelta aquí' : 'Vacío'}
                    </div>
                  )}
                  {col.items.map((c) => (
                    <BoardCard key={`${col.key}-${c.lead.id}`} c={c} />
                  ))}
                </div>
              </div>
            ))}
          </div>
          <div className="bg-card border-l border-border flex flex-col min-h-0 overflow-hidden">
            {active ? (
              <div className="flex flex-col min-h-0 h-full">
                <div className="px-3 py-2 border-b border-border shrink-0">
                  <div className="font-medium truncate">{active.first_name} {active.last_name}</div>
                  <div className="text-xs text-muted-foreground truncate">
                    {[active.phone_e164, active.email].filter(Boolean).join(' · ') || 'Sin contacto'}
                  </div>
                  <div className="flex gap-1 mt-2 flex-wrap">
                    {active.assigned_to !== me?.id && (
                      <button type="button" className="btn-primary h-8 px-2 text-xs" disabled={assigning} onClick={() => claimLead(active.id)}>
                        Tomar
                      </button>
                    )}
                    {active.inbox_resolved_at && (
                      <button type="button" className="btn-ghost h-8 px-2 text-xs" onClick={reopenConversation}>
                        Reabrir
                      </button>
                    )}
                    <Link to={`/app/leads/${active.id}`} className="btn-ghost h-8 px-2 text-xs">Ficha</Link>
                    <button type="button" className="btn-ghost h-8 px-2 text-xs" onClick={() => setViewMode('list')}>
                      Abrir hilo
                    </button>
                  </div>
                </div>
                <div className="flex-1 overflow-y-auto p-3 space-y-2">
                  {messages.slice(-12).map((m) => (
                    <div key={m.id} className={`text-xs rounded-lg px-2 py-1.5 ${m.channel === 'internal' ? 'bg-amber-500/10' : m.direction === 'out' ? 'bg-primary/10 ml-4' : 'bg-accent mr-4'}`}>
                      <div className="opacity-70 mb-0.5">{channelBadge(m.channel)} {m.author_name || ''}</div>
                      <div className="whitespace-pre-wrap line-clamp-4">{m.body || (m.media_type ? `[${m.media_type}]` : '')}</div>
                    </div>
                  ))}
                  {messages.length === 0 && <div className="text-xs text-muted-foreground">Sin mensajes</div>}
                </div>
                <div className="p-2 border-t border-border shrink-0 space-y-1">
                  <div className="flex gap-1">
                    <input
                      className="input h-9 text-sm flex-1"
                      value={body}
                      onChange={(e) => setBody(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && send()}
                      placeholder="Respuesta rápida…"
                    />
                    <button type="button" className="btn-primary h-9" disabled={sending} onClick={send}>
                      <Send className="h-4 w-4" />
                    </button>
                  </div>
                  {closeMacros.filter((m) => m.enabled !== false).slice(0, 3).map((m) => (
                    <button
                      key={m.id}
                      type="button"
                      disabled={closing}
                      className="w-full text-left text-[11px] px-2 py-1 rounded border border-border hover:bg-accent"
                      onClick={() => runCloseMacro(m.id)}
                    >
                      {m.name}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <div className="flex-1 grid place-items-center text-sm text-muted-foreground p-4">
                Selecciona una tarjeta del tablero
              </div>
            )}
          </div>
        </div>
      ) : conversations.length === 0 ? (
        <div className="flex-1 min-h-0 p-6"><EmptyState icon={MessageSquare} title="Sin conversaciones" description="Envía un WhatsApp o email." /></div>
      ) : (
        <div className="grid lg:grid-cols-[280px_1fr_300px] flex-1 min-h-0">
          <div className="bg-card border-r border-border overflow-hidden flex flex-col min-h-0">
            <div className="p-2.5 border-b border-border shrink-0 space-y-2">
              <div className="relative">
                <Search className="h-3.5 w-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <input
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                  placeholder="Buscar…"
                  className="input pl-8 h-9 text-sm"
                  data-testid="inbox-filter"
                />
              </div>
              <div className="flex gap-1">
                {[
                  ['all', 'Todos'],
                  ['whatsapp', 'WA'],
                  ['email', 'Email'],
                  ['webchat', 'Chat'],
                ].map(([v, l]) => (
                  <button
                    key={v}
                    type="button"
                    onClick={() => setChannelFilter(v)}
                    className={`flex-1 h-8 text-xs rounded-lg border ${channelFilter === v ? 'bg-primary text-primary-foreground border-primary' : 'border-border hover:bg-accent'}`}
                  >
                    {l}
                  </button>
                ))}
              </div>
              <div className="flex gap-1">
                {[
                  ['all', '1:1 y grupos'],
                  ['dm', '1:1'],
                  ['group', 'Grupos'],
                ].map(([v, l]) => (
                  <button
                    key={v}
                    type="button"
                    onClick={() => setChatType(v)}
                    className={`flex-1 h-7 text-[11px] rounded-lg border flex items-center justify-center gap-1 ${
                      chatType === v ? 'bg-emerald-600/15 text-emerald-800 border-emerald-600/30' : 'border-border hover:bg-accent'
                    }`}
                    title={v === 'group' ? 'Solo grupos de WhatsApp' : v === 'dm' ? 'Solo chats individuales' : 'Todos'}
                  >
                    {v === 'group' && <Users className="h-3 w-3" />}
                    {l}
                  </button>
                ))}
              </div>
              <select
                value={listFilter}
                onChange={(e) => setListFilter(e.target.value)}
                className="input h-8 text-xs"
                data-testid="inbox-list-filter"
              >
                <option value="all">Activas</option>
                <option value="waiting">Esperando respuesta</option>
                <option value="mine">Asignadas a mí</option>
                <option value="team">Mi equipo</option>
                <option value="unassigned">Sin asignar</option>
                <option value="unread">No leídas</option>
                <option value="resolved">Resueltas</option>
              </select>
              <div className="grid grid-cols-2 gap-1">
                <select
                  value={teamFilter}
                  onChange={(e) => setTeamFilter(e.target.value)}
                  className="input h-8 text-xs"
                  title="Filtrar por equipo"
                >
                  <option value="">Todo equipo</option>
                  {teams.map((t) => (
                    <option key={t.id} value={t.id}>{t.name}</option>
                  ))}
                </select>
                <input
                  value={skillFilter}
                  onChange={(e) => setSkillFilter(e.target.value)}
                  placeholder="Skill…"
                  className="input h-8 text-xs"
                  title="Filtrar por skill del lead"
                />
              </div>
              <label className="flex items-center gap-1.5 text-[11px] text-muted-foreground px-0.5">
                <input
                  type="checkbox"
                  checked={includeResolved || listFilter === 'resolved'}
                  disabled={listFilter === 'resolved'}
                  onChange={(e) => setIncludeResolved(e.target.checked)}
                />
                Incluir resueltas
                {(queue?.resolved > 0) && (
                  <span className="text-muted-foreground">({queue.resolved})</span>
                )}
              </label>
            </div>
            <div className="overflow-y-auto flex-1">
            {filteredConversations.length === 0 && (
              <div className="p-4 text-xs text-muted-foreground text-center">Sin resultados</div>
            )}
            {filteredConversations.map((c) => {
              const group = isGroupLead(c.lead) || c.is_group;
              const last = c.last_message || {};
              const participantPrefix =
                group && last.direction === 'in' && last.participant
                  ? `${last.participant}: `
                  : '';
              return (
              <button
                key={c.lead.id}
                data-testid="inbox-conversation"
                onClick={() => setActive(c.lead)}
                className={`w-full text-left px-3 py-2.5 border-b border-border/50 hover:bg-accent/50 transition-colors ${
                  active?.id === c.lead.id ? 'bg-accent' : ''
                } ${c.unread ? 'bg-emerald-500/[0.06]' : ''}`}
              >
                <div className="flex items-center gap-2.5">
                  <div className="relative shrink-0">
                    <WaAvatar
                      path={c.lead.wa_avatar}
                      name={leadDisplayName(c.lead)}
                      isGroup={group}
                      size={44}
                    />
                    {c.unread > 0 && (
                      <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-emerald-600 text-white text-[10px] font-bold grid place-items-center shadow-sm">
                        {c.unread > 9 ? '9+' : c.unread}
                      </span>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 min-w-0">
                      <span className={`text-[13px] truncate flex-1 ${c.unread ? 'font-semibold text-foreground' : 'font-medium'}`}>
                        {leadDisplayName(c.lead)}
                      </span>
                      <span className={`text-[10px] shrink-0 tabular-nums ${c.unread ? 'text-emerald-700 font-medium' : 'text-muted-foreground'}`}>
                        {fmtRelative(last.created_at)}
                      </span>
                    </div>
                    <div className="flex items-center gap-1 mt-0.5 min-w-0">
                      {group && (
                        <span className="text-[9px] px-1 py-px rounded bg-emerald-500/15 text-emerald-700 shrink-0 inline-flex items-center gap-0.5">
                          <Users className="h-2.5 w-2.5" /> Grupo
                        </span>
                      )}
                      {(c.channels || []).includes('email') && <Mail className="h-3 w-3 shrink-0 text-sky-500" />}
                      {(c.channels || []).includes('whatsapp') && !group && <MessageSquare className="h-3 w-3 shrink-0 text-emerald-500" />}
                      {(c.channels || []).includes('webchat') && <span className="text-[9px] px-1 rounded bg-violet-500/15 text-violet-600">Chat</span>}
                      {!c.lead.assigned_to && <span className="text-[9px] px-1 rounded bg-amber-500/15 text-amber-700">Libre</span>}
                      {(c.resolved || c.lead?.inbox_resolved_at) && (
                        <span className="text-[9px] px-1 rounded bg-slate-500/10 text-slate-600">Resuelta</span>
                      )}
                      <span className={`text-xs truncate flex-1 min-w-0 ${c.unread ? 'text-foreground/80' : 'text-muted-foreground'}`}>
                        {last.direction === 'out' && <span className="opacity-60">Tú: </span>}
                        {participantPrefix}
                        {last.media_type && <MediaTypeIcon type={last.media_type} className="h-3 w-3 inline mr-0.5 -mt-0.5" />}
                        {previewBody(last)}
                      </span>
                    </div>
                    {c.waiting && c.wait_seconds > 0 && (
                      <div className={`text-[10px] font-medium mt-0.5 ${
                        (c.sla_level === 'critical' || c.wait_seconds >= critSec)
                          ? 'text-[hsl(var(--danger))]'
                          : (c.sla_level === 'warn' || c.wait_seconds >= warnSec)
                            ? 'text-amber-600'
                            : 'text-primary'
                      }`}
                      >
                        ⏳ Espera {fmtWait(c.wait_seconds)}
                      </div>
                    )}
                  </div>
                </div>
              </button>
              );
            })}
            </div>
          </div>
          <div className="bg-background flex flex-col min-h-0 min-w-0">
            {active ? (
              <>
                <div className="px-3 py-2.5 border-b border-border flex items-center justify-between gap-2 shrink-0 bg-[#f0f2f5] dark:bg-card">
                  <div className="flex items-center gap-2.5 min-w-0">
                    <WaAvatar
                      path={active.wa_avatar}
                      name={leadDisplayName(active)}
                      isGroup={isGroupLead(active)}
                      size={40}
                    />
                    <div className="min-w-0">
                      <div className="font-medium truncate text-[15px] flex items-center gap-1.5">
                        {leadDisplayName(active)}
                        {isGroupLead(active) && (
                          <span className="text-[10px] font-normal px-1.5 py-0.5 rounded-full bg-emerald-600/15 text-emerald-800 inline-flex items-center gap-0.5">
                            <Users className="h-3 w-3" /> Grupo
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-muted-foreground truncate">
                        {isGroupLead(active)
                          ? 'Grupo de WhatsApp · responde en el hilo'
                          : ([active.phone_e164, active.email].filter(Boolean).join(' · ') || 'Sin contacto')}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    {active.assigned_to !== me?.id && (
                      <button
                        type="button"
                        className="btn-primary h-9 px-2 text-xs"
                        disabled={assigning}
                        onClick={() => claimLead(active.id)}
                        title="Asignarme esta conversación"
                      >
                        Tomar
                      </button>
                    )}
                    <Link to={`/app/leads/${active.id}`} className="btn-ghost h-9 px-2 text-xs" title="Ver ficha del lead">
                      Ficha
                    </Link>
                    {active.phone_e164 && !isGroupLead(active) && (
                      <a href={`tel:${active.phone_e164}`} className="btn-ghost h-9 px-2">
                        <Phone className="h-4 w-4" />
                      </a>
                    )}
                  </div>
                </div>
                <div
                  className="flex-1 overflow-y-auto px-3 py-3 space-y-1 min-h-0"
                  style={{
                    backgroundColor: 'hsl(var(--muted) / 0.45)',
                    backgroundImage:
                      'radial-gradient(circle at 20% 20%, hsl(var(--primary) / 0.03) 0, transparent 40%), radial-gradient(circle at 80% 60%, hsl(173 40% 40% / 0.04) 0, transparent 35%)',
                  }}
                >
                  {messages.length === 0 && (
                    <div className="text-sm text-muted-foreground text-center py-8">Sin mensajes. Elige canal WA o Email abajo.</div>
                  )}
                  {messages.map((m, idx) => {
                    const prev = messages[idx - 1];
                    const showDay = !prev || dayKey(prev.created_at) !== dayKey(m.created_at);
                    const isOut = m.direction === 'out';
                    const isInternal = m.channel === 'internal';
                    const isWa = (m.channel || 'whatsapp') === 'whatsapp';
                    const group = isGroupLead(active) || m.is_group;
                    return (
                      <div key={m.id}>
                        {showDay && (
                          <div className="flex justify-center my-3">
                            <span className="text-[11px] px-3 py-1 rounded-lg bg-card/90 border border-border/60 text-muted-foreground shadow-sm">
                              {fmtDayLabel(m.created_at)}
                            </span>
                          </div>
                        )}
                        <div
                          ref={(el) => { if (el) msgRefs.current[m.id] = el; }}
                          className={`flex group/msg ${
                            isInternal ? 'justify-center' : isOut ? 'justify-end' : 'justify-start'
                          }`}
                        >
                          <div className={`relative max-w-[min(78%,32rem)] ${isInternal ? 'w-full max-w-[95%]' : ''}`}>
                            {!isInternal && isWa && (
                              <button
                                type="button"
                                onClick={() => startReply(m)}
                                className={`absolute top-1 opacity-0 group-hover/msg:opacity-100 transition-opacity z-10 h-7 w-7 rounded-full bg-card border border-border shadow-sm grid place-items-center text-muted-foreground hover:text-foreground ${
                                  isOut ? '-left-9' : '-right-9'
                                }`}
                                title="Responder a este mensaje"
                              >
                                <Reply className="h-3.5 w-3.5" />
                              </button>
                            )}
                            <div
                              className={`rounded-lg px-2.5 py-1.5 text-[13.5px] shadow-sm ${
                                isInternal
                                  ? 'bg-amber-500/10 border border-amber-500/30 text-foreground'
                                  : isOut
                                    ? (m.channel === 'email'
                                      ? 'bg-sky-600 text-white rounded-tr-sm'
                                      : 'bg-[#d9fdd3] text-slate-900 dark:bg-emerald-900/50 dark:text-emerald-50 rounded-tr-sm')
                                    : 'bg-card text-foreground rounded-tl-sm border border-border/40'
                              }`}
                            >
                              {!isInternal && (
                                <div className="mb-0.5 flex items-center gap-1 flex-wrap">
                                  {m.channel && m.channel !== 'whatsapp' && channelBadge(m.channel)}
                                  {group && !isOut && m.participant && (
                                    <span className="text-[11px] font-semibold text-emerald-700 dark:text-emerald-400">
                                      {m.participant}
                                    </span>
                                  )}
                                  {m.subject && <span className="text-[10px] truncate opacity-80">{m.subject}</span>}
                                </div>
                              )}
                              {isInternal && (
                                <div className="mb-1 flex items-center gap-1 opacity-80">
                                  {channelBadge('internal')}
                                  <span className="text-[10px] text-muted-foreground">{m.author_name || 'Agente'}</span>
                                </div>
                              )}
                              {/* Quoted / reply preview */}
                              {(m.quoted_body || m.reply_to_id || m.quoted_msg_id) && (
                                <button
                                  type="button"
                                  onClick={() => scrollToMessage(m.reply_to_id)}
                                  className={`w-full text-left mb-1.5 rounded-md border-l-[3px] px-2 py-1 text-[12px] ${
                                    isOut
                                      ? 'border-emerald-600/70 bg-black/5 dark:bg-white/10'
                                      : 'border-primary/70 bg-muted/60'
                                  }`}
                                >
                                  <div className="font-semibold text-[11px] opacity-80 truncate">
                                    {m.quoted_participant || (m.direction === 'out' ? 'Mensaje' : 'Respondiendo')}
                                  </div>
                                  <div className="truncate opacity-80">{m.quoted_body || 'Mensaje original'}</div>
                                </button>
                              )}
                              {m.media_filename ? (
                                <ChatMedia
                                  path={m.media_filename}
                                  mediaType={m.media_type}
                                  fileName={m.media_name}
                                  mimetype={m.media_mimetype}
                                  caption={m.body}
                                />
                              ) : m.channel === 'email' && m.body?.includes('<') ? (
                                <div className="whitespace-pre-wrap text-sm" dangerouslySetInnerHTML={{ __html: m.body }} />
                              ) : (
                                <div className="whitespace-pre-wrap break-words">{m.body}</div>
                              )}
                              <div
                                className={`text-[10px] mt-0.5 flex items-center justify-end gap-1 ${
                                  isOut && isWa ? 'text-slate-600/80 dark:text-emerald-100/70' : 'text-muted-foreground'
                                }`}
                              >
                                <span>{fmtChatTime(m.created_at)}</span>
                                {m.fallback && (
                                  <a href={m.wa_link} target="_blank" rel="noreferrer" className="underline inline-flex">
                                    <ExternalLink className="h-3 w-3" />
                                  </a>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  <div ref={bottomRef} />
                </div>
                {attach && (
                  <div className="px-3 pt-2 flex items-center gap-2 border-t border-border/60 bg-card">
                    {attach.preview ? (
                      <img src={attach.preview} alt="" className="h-14 w-14 object-cover rounded-lg" />
                    ) : (
                      <div className="h-14 px-3 rounded-lg bg-accent text-xs flex items-center gap-1">
                        <MediaTypeIcon type={attach.file.type.startsWith('video') ? 'video' : attach.file.type.startsWith('audio') ? 'audio' : 'document'} />
                        {attach.file.name}
                      </div>
                    )}
                    <button type="button" onClick={clearAttach} className="btn-ghost h-8 w-8 p-0">
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                )}
                {replyTo && (
                  <div className="px-3 pt-2 border-t border-border bg-card flex items-start gap-2">
                    <div className="flex-1 min-w-0 rounded-md border-l-[3px] border-emerald-600 bg-muted/50 px-2.5 py-1.5">
                      <div className="text-[11px] font-semibold text-emerald-700 flex items-center gap-1">
                        <CornerUpLeft className="h-3 w-3" />
                        Respondiendo a {replyTo.participant || (replyTo.direction === 'out' ? 'ti' : leadDisplayName(active))}
                      </div>
                      <div className="text-xs text-muted-foreground truncate">{quotePreview(replyTo)}</div>
                    </div>
                    <button type="button" className="btn-ghost h-8 w-8 p-0 shrink-0" onClick={() => setReplyTo(null)} title="Cancelar respuesta">
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                )}
                <div className="p-3 border-t border-border shrink-0 space-y-2 bg-[#f0f2f5] dark:bg-card">
                  {visitorTyping && sendChannel === 'webchat' && (
                    <div className="text-[11px] text-muted-foreground px-1">El visitante está escribiendo…</div>
                  )}
                  <div className="flex gap-1 flex-wrap">
                    <button
                      type="button"
                      onClick={() => { setSendChannel('whatsapp'); setInternalMode(false); }}
                      className={`h-8 px-3 text-xs rounded-lg border ${!internalMode && sendChannel === 'whatsapp' ? 'bg-emerald-600 text-white border-emerald-600' : 'border-border'}`}
                    >
                      WhatsApp
                    </button>
                    <button
                      type="button"
                      onClick={() => { setSendChannel('email'); setInternalMode(false); }}
                      disabled={!active.email}
                      className={`h-8 px-3 text-xs rounded-lg border ${!internalMode && sendChannel === 'email' ? 'bg-sky-600 text-white border-sky-600' : 'border-border'} disabled:opacity-40`}
                      title={active.email || 'Sin email'}
                    >
                      Email
                    </button>
                    <button
                      type="button"
                      onClick={() => { setSendChannel('webchat'); setInternalMode(false); }}
                      className={`h-8 px-3 text-xs rounded-lg border ${!internalMode && sendChannel === 'webchat' ? 'bg-violet-600 text-white border-violet-600' : 'border-border'}`}
                    >
                      Webchat
                    </button>
                    <button
                      type="button"
                      onClick={() => setInternalMode((v) => !v)}
                      className={`h-8 px-3 text-xs rounded-lg border flex items-center gap-1 ${internalMode ? 'bg-amber-600 text-white border-amber-600' : 'border-border'}`}
                      title="Nota interna (solo agentes)"
                    >
                      <StickyNote className="h-3 w-3" /> Interna
                    </button>
                  </div>
                  {internalMode && (
                    <div className="text-[11px] text-amber-700 bg-amber-500/10 px-2 py-1 rounded">
                      Nota interna: no se envía al cliente. Puedes @mencionar colegas.
                    </div>
                  )}
                  {sendChannel === 'email' && (
                    <input
                      value={emailSubject}
                      onChange={(e) => setEmailSubject(e.target.value)}
                      placeholder="Asunto del correo"
                      className="input h-9 text-sm"
                    />
                  )}
                  {showQuick && (
                    <div className="border border-border rounded-[10px] max-h-44 overflow-y-auto bg-card shadow-sm">
                      {quickReplies.length === 0 ? (
                        <div className="p-3 text-xs text-muted-foreground">
                          Sin respuestas. Crea en menú <Link to="/app/quick-replies" className="underline">Respuestas rápidas</Link>.
                        </div>
                      ) : (
                        quickReplies
                          .filter((q) => !q.channels?.length || q.channels.includes(sendChannel) || q.channels.includes('all'))
                          .map((q) => (
                            <button
                              key={q.id}
                              type="button"
                              className="w-full text-left px-3 py-2 text-sm hover:bg-accent border-b border-border/50 last:border-0"
                              onClick={() => applyQuickReply(q)}
                            >
                              <div className="font-medium flex items-center gap-2">
                                {q.title}
                                {q.shortcut && <span className="font-mono text-[10px] text-muted-foreground">{q.shortcut}</span>}
                              </div>
                              <div className="text-xs text-muted-foreground truncate">{q.body}</div>
                            </button>
                          ))
                      )}
                    </div>
                  )}
                  <div className="flex gap-2">
                    {(sendChannel === 'whatsapp' || sendChannel === 'webchat') && (
                      <>
                        <input
                          ref={fileRef}
                          type="file"
                          accept={sendChannel === 'webchat' ? 'image/*' : 'image/*,video/*,audio/*'}
                          className="hidden"
                          onChange={pickFile}
                        />
                        <button
                          type="button"
                          data-testid="inbox-attach-btn"
                          onClick={() => fileRef.current?.click()}
                          className="btn-ghost h-10 w-10 p-0 shrink-0"
                          title={sendChannel === 'webchat' ? 'Adjuntar imagen' : 'Adjuntar foto, video o audio'}
                        >
                          <ImagePlus className="h-4 w-4" />
                        </button>
                      </>
                    )}
                    <button
                      type="button"
                      className={`btn-ghost h-10 w-10 p-0 shrink-0 ${showQuick ? 'bg-accent' : ''}`}
                      title="Respuestas rápidas"
                      onClick={() => setShowQuick((v) => !v)}
                    >
                      <Zap className="h-4 w-4" />
                    </button>
                    <input
                      data-testid="inbox-input"
                      value={body}
                      onChange={(e) => setBody(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && send()}
                      placeholder={
                        internalMode
                          ? 'Nota interna (solo equipo)…'
                          : sendChannel === 'email'
                            ? 'Cuerpo del email…'
                            : sendChannel === 'webchat'
                              ? 'Responder en webchat…'
                              : 'Mensaje o /atajo…'
                      }
                      className={`input flex-1 ${internalMode ? 'border-amber-500/50' : ''}`}
                    />
                    <button data-testid="inbox-send-btn" onClick={send} disabled={sending} className="btn-primary">
                      <Send className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </>
            ) : (
              <div className="flex-1 grid place-items-center text-muted-foreground text-sm">Selecciona una conversación</div>
            )}
          </div>
          <div className="bg-card border-l border-border p-3 overflow-y-auto min-h-0">
            {active && (
              <div className="mb-4 pb-4 border-b border-border space-y-3">
                <div>
                  <h3 className="font-display font-semibold mb-2 flex items-center gap-1.5 text-sm">
                    <Star className="h-4 w-4" /> CSAT
                  </h3>
                  {csat?.score != null ? (
                    <div className="text-sm">
                      <span className="font-semibold text-amber-600">{'★'.repeat(csat.score)}</span>
                      <span className="text-muted-foreground text-xs ml-1">{csat.score}/5</span>
                      {csat.at && <div className="text-[10px] text-muted-foreground">{fmtRelative(csat.at)}</div>}
                    </div>
                  ) : (
                    <button type="button" className="btn-ghost h-8 text-xs w-full justify-start" onClick={requestCsat}>
                      Solicitar calificación (webchat)
                    </button>
                  )}
                </div>
                <div>
                  <h3 className="text-sm font-medium mb-1">Skills del lead</h3>
                  <input
                    className="input h-8 text-xs"
                    value={leadSkills}
                    onChange={(e) => setLeadSkills(e.target.value)}
                    placeholder="ventas, soporte…"
                  />
                  <div className="flex gap-1 mt-1 flex-wrap">
                    <button type="button" className="btn-ghost h-7 text-xs" onClick={saveLeadSkills}>
                      Guardar
                    </button>
                    <button
                      type="button"
                      className="btn-ghost h-7 text-xs"
                      disabled={assigning}
                      onClick={assignBySkill}
                      title="Round-robin entre agentes con ese skill"
                    >
                      <Shuffle className="h-3 w-3" /> Asignar por skill
                    </button>
                  </div>
                </div>

                <div>
                  <h3 className="font-display font-semibold mb-2 flex items-center gap-1.5 text-sm">
                    <CheckCircle2 className="h-4 w-4" /> Macros de cierre
                  </h3>
                  {active.inbox_resolved_at && (
                    <button type="button" className="btn-primary h-8 text-xs w-full mb-2" onClick={reopenConversation}>
                      Reabrir en cola
                    </button>
                  )}
                  {closeMacros.length === 0 ? (
                    <div className="space-y-1">
                      <button type="button" className="btn-ghost h-8 text-xs w-full" onClick={seedCloseMacros}>
                        Cargar macros ejemplo
                      </button>
                      <Link to="/app/close-macros" className="text-[11px] text-primary underline block text-center">
                        Administrar macros
                      </Link>
                    </div>
                  ) : (
                    <div className="space-y-1">
                      {closeMacros.filter((m) => m.enabled !== false).map((m) => (
                        <button
                          key={m.id}
                          type="button"
                          disabled={closing}
                          className="w-full text-left text-xs px-2 py-1.5 rounded-lg border border-border hover:bg-accent"
                          onClick={() => runCloseMacro(m.id)}
                        >
                          <div className="font-medium">{m.name}</div>
                          <div className="text-[10px] text-muted-foreground">
                            {[
                              m.request_csat && 'CSAT',
                              m.internal_note && 'nota',
                              m.status && m.status,
                              m.mark_resolved && 'resuelto',
                            ].filter(Boolean).join(' · ')}
                          </div>
                        </button>
                      ))}
                      <Link to="/app/close-macros" className="text-[11px] text-primary underline block text-center pt-1">
                        Administrar macros
                      </Link>
                    </div>
                  )}
                </div>

                <h3 className="font-display font-semibold mb-2 flex items-center gap-1.5">
                  <UserPlus className="h-4 w-4" /> Asignar / transferir
                </h3>
                <select
                  data-testid="inbox-assign"
                  className="input text-sm"
                  disabled={assigning || !active}
                  value={active.assigned_to || ''}
                  onChange={async (e) => {
                    const to = e.target.value || null;
                    if (!to) {
                      assignLead(null);
                      return;
                    }
                    setAssigning(true);
                    try {
                      await api.post('/webchat/transfer', { lead_id: active.id, to_user_id: to });
                      toast.success('Chat transferido');
                      setActive((prev) => (prev ? { ...prev, assigned_to: to } : prev));
                      setConversations((prev) =>
                        prev.map((c) =>
                          c.lead?.id === active.id
                            ? { ...c, lead: { ...c.lead, assigned_to: to } }
                            : c,
                        ),
                      );
                      loadMessages(active.id);
                      loadInbox(true);
                    } catch {
                      await assignLead(to);
                    } finally {
                      setAssigning(false);
                    }
                  }}
                >
                  <option value="">Sin asignar</option>
                  {users.map((u) => (
                    <option key={u.id} value={u.id}>{u.name || u.email}</option>
                  ))}
                </select>
                <p className="text-[11px] text-muted-foreground mt-1">
                  Transferir notifica al nuevo agente y deja nota en el hilo webchat.
                </p>
                {active.email && (
                  <p className="text-[11px] text-muted-foreground mt-2 truncate">{active.email}</p>
                )}
              </div>
            )}
            <h3 className="text-xs font-semibold text-muted-foreground mb-2">Plantillas</h3>
            {templates.length === 0 && <div className="text-xs text-muted-foreground">Sin plantillas</div>}
            <div className="space-y-1">
              {templates
                .filter((t) => t.enabled)
                .map((t) => (
                  <button
                    key={t.id}
                    onClick={() => active && sendTpl(t)}
                    disabled={!active}
                    className="w-full text-left p-2 rounded-lg border border-border hover:bg-accent text-sm"
                  >
                    <div className="font-medium flex items-center gap-1 truncate">
                      {t.name}
                      {t.media_filename && <MediaTypeIcon type={t.media_type || 'image'} className="h-3.5 w-3.5 opacity-70" />}
                    </div>
                  </button>
                ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
